import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import './DataTable.css';
import ArticleDetail from './ArticleDetail';
import HeartIcon from './HeartIcon';
import BookmarkIcon from './BookmarkIcon';
import DatabaseViewMenu from './DatabaseViewMenu';
import { readArticleService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ITEMS_PER_PAGE = 20;
const STORAGE_KEYS = {
  density: 'database.density'
};




const normalizeId = (value) => {
  if (value === undefined || value === null) {
    return null;
  }
  return String(value);
};

const DataTable = React.memo(function DataTable({
  data,
  onRefresh,
  showTags = {},
  conferenceMode = false,
  attendedConferenceMap = {},
  onToggleConferenceAttendance,
  activeFilters: activeFiltersProp,
  onRemoveFilter,
  tagStylePreference = 'colored',
  density: densityProp,
  onDensityChange,
  viewMode = 'list',
  onViewModeChange,
  savedFiltersPanel,
  onFavorite,
  onReadLater,
  onPatentLinkRestricted,
  renderHeaderInParent = false,
  sortConfig: sortConfigProp,
  onSortChange
}) {
  const { currentUser } = useAuth();
  const plan = currentUser?.plan || 'none';
  const isUserLocked = !currentUser || plan === 'none';
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [favorites, setFavorites] = useState({});
  const [readLaterItems, setReadLaterItems] = useState({});
  const isDensityControlled = densityProp === 'compact' || densityProp === 'detailed';
  const [densityState, setDensityState] = useState(() => {
    if (typeof window === 'undefined') {
      return 'detailed';
    }
    const stored = window.localStorage.getItem(STORAGE_KEYS.density);
    return stored === 'compact' ? 'compact' : 'detailed';
  });
  const density = isDensityControlled ? densityProp : densityState;

  // Use controlled sortConfig if provided, otherwise use internal state
  const isSortControlled = sortConfigProp != null;
  const [sortConfigState, setSortConfigState] = useState({ key: 'createdAt', direction: 'descending' });
  const sortConfig = isSortControlled ? sortConfigProp : sortConfigState;
  const [currentPage, setCurrentPage] = useState(1);
  const [readArticles, setReadArticles] = useState({});
  const selectedArticleRef = useRef(null);
  const tagStyle = tagStylePreference === 'neutral' ? 'neutral' : 'colored';
  const savedFiltersContent = savedFiltersPanel ?? null;

  useEffect(() => {
    if (isDensityControlled) {
      return;
    }
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEYS.density, densityState);
    }
  }, [densityState, isDensityControlled]);

  const updateDensity = (nextDensity) => {
    if (nextDensity !== 'compact' && nextDensity !== 'detailed') {
      return;
    }
    if (!isDensityControlled) {
      setDensityState(nextDensity);
    }
    if (typeof onDensityChange === 'function') {
      onDensityChange(nextDensity);
    }
  };

  const handleViewMenuSelect = (value) => {
    if (value === 'card') {
      if (typeof onViewModeChange === 'function' && viewMode !== 'card') {
        onViewModeChange('card');
      }
      return;
    }
    updateDensity(value);
    if (typeof onViewModeChange === 'function' && viewMode !== 'list') {
      onViewModeChange('list');
    }
  };


  const getActiveFiltersFromUrl = () => {
    if (typeof window === 'undefined') {
      return {};
    }
    const params = new URLSearchParams(window.location.search);
    const yearMin = params.get('year_min');
    const yearMax = params.get('year_max');
    const filters = {
      OEM: params.getAll('OEM'),
      brand: params.getAll('brand'),
      car_model: params.getAll('car_model'),
      system: params.getAll('System'),
      type: params.getAll('Type'),
      material: params.getAll('material'),
      technology: params.getAll('technology'),
      subsystem: [...params.getAll('Subsystem'), ...params.getAll('subsystem')],
      part: params.getAll('part')
    };
    const status = params.get('status');
    if (status) {
      filters.status = status;
    }
    const query = params.get('query');
    if (query) {
      filters.query = query;
    }
    if (yearMin && yearMax) {
      filters.year_of_publication = [parseInt(yearMin, 10), parseInt(yearMax, 10)];
    }
    return filters;
  };

  const resolvedFilters = activeFiltersProp !== undefined
    ? activeFiltersProp || {}
    : getActiveFiltersFromUrl();

  const rawStatusFilter = resolvedFilters.status || '';
  const statusFilter = rawStatusFilter === 'new' ? 'unread' : rawStatusFilter;

  const appliedFilterChips = useMemo(() => {
    const chips = [];
    if (!resolvedFilters) {
      return chips;
    }

    const seen = new Set();

    const listFilters = [
      { key: 'OEM', label: 'OEM' },
      { key: 'brand', label: 'Brand' },
      { key: 'car_model', label: 'Car Model' },
      { key: 'system', label: 'System' },
      { key: 'System', label: 'System' },
      { key: 'subsystem', label: 'Subsystem' },
      { key: 'Subsystem', label: 'Subsystem' },
      { key: 'part', label: 'Part' },
      { key: 'type', label: 'Type' },
      { key: 'material', label: 'Material' },
      { key: 'technology', label: 'Technology' }
    ];

    listFilters.forEach(({ key, label }) => {
      const values = resolvedFilters[key];
      if (Array.isArray(values) && values.length > 0) {
        values
          .filter((item) => item !== undefined && item !== null && item !== '')
          .forEach((value) => {
            const id = `${label}-${value}`;
            if (seen.has(id)) {
              return;
            }
            seen.add(id);
            const valueLabel = `${value}`;
            chips.push({
              key,
              label,
              valueText: valueLabel,
              removableValue: value
            });
          });
      }
    });

    const statusLabels = { unread: 'Non lu', read: 'Lu' };
    const rawStatusValue = resolvedFilters.status;
    const statusValue = rawStatusValue === 'new' ? 'unread' : rawStatusValue;
    if (statusValue) {
      const id = `Statut-${statusValue}`;
      if (!seen.has(id)) {
        seen.add(id);
        chips.push({
          key: 'status',
          label: 'Statut',
          valueText: statusLabels[statusValue] || statusValue,
          removableValue: statusValue
        });
      }
    }
    const yearRange = resolvedFilters.year_of_publication;
    if (Array.isArray(yearRange) && yearRange.length === 2) {
      const valueText = `${yearRange[0]} - ${yearRange[1]}`;
      const id = `Ann�es-${valueText}`;
      if (!seen.has(id)) {
        seen.add(id);
        chips.push({
          key: 'year_of_publication',
          label: 'Ann�es',
          valueText,
          removableValue: undefined
        });
      }
    }

    return chips;
  }, [resolvedFilters]);

  const handleConferenceAttendanceChange = useCallback((conference, nextValue) => {
    if (typeof onToggleConferenceAttendance === 'function') {
      onToggleConferenceAttendance(conference, nextValue);
    }
  }, [onToggleConferenceAttendance]);

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    const sortableData = [...data];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [data, sortConfig]);

  useEffect(() => {
    async function fetchReadArticles() {
      try {
        const readIds = await readArticleService.getReadArticles();
        const readMap = {};
        (Array.isArray(readIds) ? readIds : []).forEach((id) => {
          const key = normalizeId(id);
          if (key) {
            readMap[key] = true;
          }
        });
        setReadArticles(readMap);
      } catch (error) {
        console.error('Erreur chargement articles lus:', error);
      }
    }

    if (!conferenceMode) {
      fetchReadArticles();
    }
  }, [conferenceMode]);


  const filteredData = useMemo(() => {
    if (!statusFilter) {
      return sortedData;
    }

    return sortedData.filter((item) => {
      const rawId = item._id ?? item.ID ?? item.id;
      const legacyId = item.ID ?? item.id ?? null;
      const normalizedId = normalizeId(rawId ?? legacyId);
      const readFlag = normalizedId ? readArticles[normalizedId] : false;
      const isRead = Boolean(readFlag || item.isRead);

      if (statusFilter === 'read') {
        return isRead;
      }
      if (statusFilter === 'unread') {
        return !isRead;
      }
      return true;
    });
  }, [sortedData, statusFilter, readArticles]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (conferenceMode) {
      return;
    }
    const maxPage = Math.max(1, Math.ceil(filteredData.length / ITEMS_PER_PAGE));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [filteredData.length, conferenceMode, currentPage]);

  const paginatedData = useMemo(() => {
    if (conferenceMode) {
      return filteredData;
    }
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredData, currentPage, conferenceMode]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSortChange = (event) => {
    const value = event.target.value;
    if (!value) {
      const defaultSort = { key: 'createdAt', direction: 'descending' };
      if (isSortControlled && onSortChange) {
        onSortChange(defaultSort);
      } else {
        setSortConfigState(defaultSort);
      }
      return;
    }
    const [key, direction] = value.split(':');
    const newSort = { key, direction };
    if (isSortControlled && onSortChange) {
      onSortChange(newSort);
    } else {
      setSortConfigState(newSort);
    }
  };

  const handleFavoriteChange = (articleId, isFavorite) => {
    setFavorites(prev => ({
      ...prev,
      [articleId]: isFavorite
    }));

    if (selectedArticle && selectedArticle.ID === articleId) {
      const updatedArticle = {
        ...selectedArticle,
        isFavorite
      };
      selectedArticleRef.current = updatedArticle;
    }

    if (onRefresh) {
      onRefresh();
    }
  };

  const handleReadLaterChange = (articleId, isReadLater) => {
    setReadLaterItems(prev => ({
      ...prev,
      [articleId]: isReadLater
    }));

    if (selectedArticle && selectedArticle.ID === articleId) {
      const updatedArticle = {
        ...selectedArticle,
        isReadLater
      };
      selectedArticleRef.current = updatedArticle;
    }

    if (onRefresh) {
      onRefresh();
    }
  };

  const updateReadStatus = useCallback(async (articleId, legacyId, shouldRead) => {
    const preferredId = articleId ?? legacyId;
    const key = normalizeId(preferredId);
    if (!key) {
      return;
    }

    const previous = Boolean(readArticles[key]);
    setReadArticles((prev) => {
      const next = { ...prev };
      if (shouldRead) {
        next[key] = true;
      } else {
        delete next[key];
      }
      return next;
    });

    if (selectedArticleRef.current) {
      const current = selectedArticleRef.current;
      const currentId = normalizeId(current._id ?? current.ID ?? current.id);
      if (currentId === key) {
        const nextArticle = { ...current, isRead: shouldRead };
        selectedArticleRef.current = nextArticle;
        setSelectedArticle(nextArticle);
      }
    }

    try {
      const primaryId = articleId ?? legacyId;
      await (shouldRead
        ? readArticleService.markAsRead(primaryId, legacyId)
        : readArticleService.markAsUnread(primaryId, legacyId));
    } catch (error) {
      console.error('Error updating reading status:', error);
      setReadArticles((prev) => {
        const next = { ...prev };
        if (previous) {
          next[key] = true;
        } else {
          delete next[key];
        }
        return next;
      });
      if (selectedArticleRef.current) {
        const current = selectedArticleRef.current;
        const currentId = normalizeId(current._id ?? current.ID ?? current.id);
        if (currentId === key) {
          const revertedArticle = { ...current, isRead: previous };
          selectedArticleRef.current = revertedArticle;
          setSelectedArticle(revertedArticle);
        }
      }
    }
  }, [readArticles, setSelectedArticle]);

  const markAsRead = useCallback((articleId, legacyId) => updateReadStatus(articleId, legacyId, true), [updateReadStatus]);
  const markAsUnread = useCallback((articleId, legacyId) => updateReadStatus(articleId, legacyId, false), [updateReadStatus]);

  const handleRemoveAppliedFilter = (filterKey, filterValue) => {
    if (typeof onRemoveFilter !== 'function') {
      return;
    }
    onRemoveFilter(filterKey, filterValue);
  };

  const handleOpenDetail = (article) => {
    const identifier = article._id ?? article.ID ?? article.id;
    const legacyIdentifier = article.ID ?? article.id ?? null;
    if (identifier) {
      markAsRead(identifier, legacyIdentifier);
    }

    const articleWithStates = {
      ...article,
      isFavorite: favorites[article.ID] !== undefined ? favorites[article.ID] : article.isFavorite,
      isReadLater: readLaterItems[article.ID] !== undefined ? readLaterItems[article.ID] : article.isReadLater,
      isRead: true,
    };

    selectedArticleRef.current = articleWithStates;
    setSelectedArticle(articleWithStates);
  };

  const handleCloseDetail = () => {
    setSelectedArticle(null);
    selectedArticleRef.current = null;
  };

  const getTagColor = (type) => {
    switch (type) {
      case 'OEM':
        return 'tag-oem';
      case 'brand':
        return 'tag-brand';
      case 'car_model':
        return 'tag-car-model';
      case 'system':
      case 'System':
        return 'tag-system';
      case 'subsystem':
      case 'Subsystem':
        return 'tag-subsystem';
      case 'part':
        return 'tag-part';
      case 'Type':
        return 'tag-type';
      case 'Material':
        return 'tag-material';
      case 'technology':
        return 'tag-technology';
      default:
        return '';
    }
  };

  

  const getTagClassName = (colorType, filterTypeArg, value) => {
    const classes = ['tag'];
    if (tagStyle === 'colored') {
      const colorClass = getTagColor(colorType);
      if (colorClass) {
        classes.push(colorClass);
      }
    } else {
      classes.push('tag-neutral');
    }

    const type = filterTypeArg ?? colorType;
    let isSelected = false;

    const selected = resolvedFilters[type];
    if (Array.isArray(selected) && selected.includes(value)) {
      isSelected = true;
    } else if (type === 'subsystem') {
      const legacy = resolvedFilters.Subsystem;
      if (Array.isArray(legacy) && legacy.includes(value)) {
        isSelected = true;
      }
    } else if (type === 'system') {
      const legacySystem = resolvedFilters.System;
      if (Array.isArray(legacySystem) && legacySystem.includes(value)) {
        isSelected = true;
      }
    } else if (type === 'System') {
      const normalized = resolvedFilters.system;
      if (Array.isArray(normalized) && normalized.includes(value)) {
        isSelected = true;
      }
    }

    if (isSelected) {
      classes.push('tag-selected');
    }
    return classes.join(' ');
  };

  const renderConferenceRows = () => {
    if (data.length === 0) {
      return (
        <tr>
          <td colSpan={9} style={{ textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
            No conference found
          </td>
        </tr>
      );
    }

    return data.map(conf => {
      const rawId = conf._id || conf.id || '';
      const normalizedId = rawId ? String(rawId) : '';
      const isAttending = (attendedConferenceMap && attendedConferenceMap[normalizedId] !== undefined)
        ? attendedConferenceMap[normalizedId]
        : Boolean(conf.isAttending);

      return (
        <tr
          key={conf._id || conf.id}
          style={{ cursor: conf.Link ? 'pointer' : 'default' }}
          onClick={() => { if (conf.Link) window.open(conf.Link, '_blank'); }}
        >
          <td>{conf.date_debut2 ? new Date(conf.date_debut2).toLocaleDateString() : ''}</td>
          <td>{conf.date_fin3 ? new Date(conf.date_fin3).toLocaleDateString() : ''}</td>
          <td>{conf.name || ''}</td>
          <td>{conf.Company || ''}</td>
          <td>{conf.Country || ''}</td>
          <td>{conf.Price || ''}</td>
          <td>{conf.type || ''}</td>
          <td>{conf.presence || ''}</td>
          <td className="attendance-cell" onClick={event => event.stopPropagation()}>
            <input
              type="checkbox"
              className="attendance-checkbox"
              checked={isAttending}
              onChange={event => {
                event.stopPropagation();
                handleConferenceAttendanceChange(conf, event.target.checked);
              }}
              onClick={event => event.stopPropagation()}
              aria-label="Marquer cette conference comme suivie"
            />
          </td>
        </tr>
      );
    });
  };

  if (conferenceMode) {
    return (
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date debut</th>
              <th>Date fin</th>
              <th>Nom</th>
              <th>Company</th>
              <th>Country</th>
              <th>Price</th>
              <th>Type</th>
              <th>Presence</th>
              <th>Participation</th>
            </tr>
          </thead>
          <tbody>{renderConferenceRows()}</tbody>
        </table>
      </div>
    );
  }

  const totalItems = filteredData.length;
  const hasResults = totalItems > 0;
  const rangeStart = hasResults ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0;
  const rangeEnd = hasResults ? Math.min(currentPage * ITEMS_PER_PAGE, totalItems) : 0;

  const renderArticleCards = () => {
    if (paginatedData.length === 0) {
      return <div className="database-no-results">No results found</div>;
    }

    return paginatedData.map((item, idx) => {
      const isFavorite = favorites[item.ID] !== undefined ? favorites[item.ID] : item.isFavorite;
      const isReadLater = readLaterItems[item.ID] !== undefined ? readLaterItems[item.ID] : item.isReadLater;
      const rawId = item._id ?? item.ID ?? item.id;
      const legacyId = item.ID ?? item.id ?? null;
      const normalizedId = normalizeId(rawId ?? legacyId);
      const readFlag = normalizedId ? readArticles[normalizedId] : false;
      const isRead = Boolean(readFlag || item.isRead);

      let dateAjout = '';
      const createdAtValue = item.createdAt || item.createdAT;
      if (createdAtValue) {
        const createdDate = new Date(createdAtValue);
        dateAjout = createdDate.toLocaleDateString('fr-FR');
      }

      let publication = '';
      if (item.month_of_publication && item.year_of_publication) {
        publication = `${item.month_of_publication}/${item.year_of_publication}`;
      } else if (item.year_of_publication) {
        publication = String(item.year_of_publication);
      }

      const tagData = [];
      if (showTags.OEM !== false && item.OEM) {
        tagData.push({
          key: `tag-oem-${item.OEM}`,
          className: getTagClassName('OEM', 'OEM', item.OEM),
          label: item.OEM,
        });
      }
      if (item.brand && showTags.brand !== false) {
        tagData.push({
          key: `tag-brand-${item.brand}`,
          className: getTagClassName('brand', 'brand', item.brand),
          label: item.brand,
        });
      }
      if (item.car_model && showTags.car_model !== false) {
        tagData.push({
          key: `tag-car_model-${item.car_model}`,
          className: getTagClassName('car_model', 'car_model', item.car_model),
          label: item.car_model,
        });
      }
      if (showTags.System !== false && item.System) {
        tagData.push({
          key: `tag-system-${item.System}`,
          className: getTagClassName('system', 'system', item.System),
          label: item.System,
        });
      }
      const subsystemValue = item.subsystem || item.Subsystem;
      if (subsystemValue && showTags.subsystem !== false) {
        tagData.push({
          key: `tag-subsystem-${subsystemValue}`,
          className: getTagClassName('subsystem', 'subsystem', subsystemValue),
          label: subsystemValue,
        });
      }
      if (item.part && showTags.part !== false) {
        tagData.push({
          key: `tag-part-${item.part}`,
          className: getTagClassName('part', 'part', item.part),
          label: item.part,
        });
      }
      if (showTags.Type !== false && item.Type) {
        tagData.push({
          key: `tag-type-${item.Type}`,
          className: getTagClassName('Type', 'type', item.Type),
          label: item.Type,
        });
      }
      if (item.material && showTags.Material !== false) {
        tagData.push({
          key: `tag-material-${item.material}`,
          className: getTagClassName('Material', 'material', item.material),
          label: item.material,
        });
      }
      if (item.technology && showTags.technology !== false) {
        tagData.push({
          key: `tag-technology-${item.technology}`,
          className: getTagClassName('technology', 'technology', item.technology),
          label: item.technology,
        });
      }

      const tagsInline = tagData.length > 0 ? (
        <div className={`result-card-tags-inline ${density === 'compact' ? 'is-compact' : ''}`}>
          {tagData.map(({ key, className, label }) => (
            <span key={`inline-${key}`} className={className}>
              {label}
            </span>
          ))}
        </div>
      ) : null;

      const headerTags = tagData.length > 0 ? (
        <div className="result-card-tags-row">
          {tagData.map(({ key, className, label }) => (
            <span key={`header-${key}`} className={className}>
              {label}
            </span>
          ))}
        </div>
      ) : null;

      const actions = (
        <div
          className={`result-card-actions ${density === 'compact' ? 'compact' : ''}`}
          onClick={event => event.stopPropagation()}
        >
          <div
            className={`result-action ${isFavorite ? 'is-active' : ''}`}
            title={isFavorite ? 'Favori' : 'Ajouter aux favoris'}
            onClick={(event) => {
              event.stopPropagation();
              if (onFavorite) {
                onFavorite(item);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <HeartIcon filled={isFavorite} />
          </div>
          <div
            className={`result-action ${isReadLater ? 'is-active' : ''}`}
            title={isReadLater ? 'Dans la liste de lecture' : 'Lire plus tard'}
            onClick={(event) => {
              event.stopPropagation();
              if (onReadLater) {
                onReadLater(item);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <BookmarkIcon filled={isReadLater} />
          </div>
          <div
            className={`result-action result-action-read ${isRead ? 'is-read' : 'is-unread'}`}
            onClick={(event) => {
              event.stopPropagation();
              if (rawId !== undefined && rawId !== null) {
                if (isRead) {
                  markAsUnread(rawId, legacyId);
                } else {
                  markAsRead(rawId, legacyId);
                }
              }
            }}
            title={isRead ? 'Marquer comme non lu' : 'Marquer comme lu'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="1.6" fill={isRead ? 'currentColor' : 'none'} />
            </svg>
          </div>
          {item.Link && (
            <a
              href={item.Link}
              target="_blank"
              rel="noopener noreferrer"
              className="result-action result-action-link"
              title="Ouvrir le lien"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                const isPatent = item.Type === 'Patent';
                const shouldBlock = isPatent && isUserLocked;

                if (shouldBlock) {
                  if (onPatentLinkRestricted) {
                    onPatentLinkRestricted();
                  }
                } else {
                  window.open(item.Link, '_blank', 'noopener,noreferrer');
                }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M14 4H20V10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10 14L20 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M20 20H6C5.46957 20 4.96086 19.7893 4.58579 19.4142C4.21071 19.0391 4 18.5304 4 18V4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          )}
        </div>
      );

      const compactHeaderRow = (
        <div className={`result-card-title-row ${density === 'compact' ? 'is-compact' : ''}`}>
          <h3 className="result-card-title">{item.Title}</h3>
          {tagsInline}
          {actions}
        </div>
      );

      const publicationRow = publication
        ? (
          <div className="result-card-calendar-row">
            <span className="result-card-calendar-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7 3v4M17 3v4M4 11h16M6 21h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="result-card-calendar-text">{publication}</span>
          </div>
        )
        : null;

      const detailedLayoutClassName = ['result-card-detailed-layout'];
      if (!item.Picture) {
        detailedLayoutClassName.push('no-image');
      }
      if (density === 'compact') {
        return (
          <article
            key={item.ID || item._id || idx}
            className="database-result-card is-compact"
            onClick={() => handleOpenDetail(item)}
          >
            <div className="result-card-main is-compact">
              {compactHeaderRow}
            </div>
          </article>
        );
      }

      return (
        <article
          key={item.ID || item._id || idx}
          className="database-result-card"
          onClick={() => handleOpenDetail(item)}
        >
          <div className="result-card-main">
            <div className={detailedLayoutClassName.join(' ')}>
              {item.Picture && (
                <div className="result-card-image result-card-image--detailed">
                  <img src={item.Picture} alt={item.Title} loading="lazy" />
                </div>
              )}

              <div className="result-card-content">
                <div className="result-card-header-line">
                  <div className="result-card-header-info">
                    <h3 className="result-card-title">{item.Title}</h3>
                  </div>
                  <div className="result-card-header-actions">
                    {actions}
                  </div>
                </div>
                {density === 'detailed' && item.short_resume && (
                  <p className="result-card-summary">{item.short_resume}</p>
                )}
                {headerTags}
                {publicationRow}
              </div>
            </div>
          </div>
        </article>
      );
    });
  };

  const resultsHeaderContent = (
    <div className={`database-results-top ${savedFiltersContent ? 'has-saved-filters' : ''}`}>
      {savedFiltersContent}
      <div className="database-results-header">
        <div className="database-results-info">
          <div className="database-results-count">
            {hasResults
              ? `Results ${rangeStart}-${rangeEnd} of ${totalItems}`
              : 'No results found'}
          </div>
          {appliedFilterChips.length > 0 && (
            <div className="database-active-filters">
              {appliedFilterChips.map(({ key, label, valueText, removableValue }) => (
                <span key={`${key}-${valueText ?? 'all'}`} className="database-filter-chip">
                  <span className="database-filter-chip-label">{label}</span>
                  {valueText && <span className="database-filter-chip-value">{valueText}</span>}
                  {typeof onRemoveFilter === 'function' && (
                    <button
                      type="button"
                      className="filter-chip-remove"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRemoveAppliedFilter(key, removableValue);
                      }}
                      aria-label={`Retirer le filtre ${label}${valueText ? ` ${valueText}` : ''}`}
                    >
                      &times;
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="database-header-actions">
          <div className="database-sort">
            <label htmlFor="database-sort-select" className="database-sort-label">Sort by</label>
            <select
              id="database-sort-select"
              className="database-sort-select"
              value={`${sortConfig.key}:${sortConfig.direction}`}
              onChange={handleSortChange}
            >
              <option value="createdAt:descending">Most recent</option>
              <option value="createdAt:ascending">Oldest</option>
              <option value="Title:ascending">Title A-Z</option>
            </select>
          </div>
          <DatabaseViewMenu
            selected={viewMode === 'card' ? 'card' : density}
            onSelect={handleViewMenuSelect}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="database-results-container">
      {!renderHeaderInParent && resultsHeaderContent}

      <div className={`database-results-list ${density === 'compact' ? 'is-compact' : ''}`}>
        {renderArticleCards()}
      </div>

      {totalPages > 1 && (
        <div className="database-pagination">
          <button
            type="button"
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Prec
          </button>
          {Array.from({ length: totalPages }, (_, index) => index + 1).map(page => (
            <button
              key={page}
              type="button"
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          <button
            type="button"
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Suiv
          </button>
        </div>
      )}

      {selectedArticle && (
        <ArticleDetail
          key={`article-detail-${selectedArticle.ID}-${selectedArticle.isFavorite}-${selectedArticle.isReadLater}`}
          article={selectedArticle}
          onClose={handleCloseDetail}
          onFavoriteChange={handleFavoriteChange}
          onReadLaterChange={handleReadLaterChange}
          onRead={markAsRead}
        />
      )}
    </div>
  );
});

export default DataTable;






















